import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { clearAuthExtras } from '../utils/permissoes';
import { clearProdutosAtivos } from '../utils/produtosRevenda';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

export const clearAuthSession = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('userName');
  sessionStorage.removeItem('portalTipo');
  sessionStorage.removeItem('tokenZenTag');
  clearAuthExtras();
  clearProdutosAtivos();
};

let interceptorsConfigured = false;
let onSessionLostHandler = null;

const isAuthEndpoint = (url = '') =>
  /\/account\/(login|revenda\/login|distribuidor\/login)/i.test(url);

export const setupInterceptors = (navigate, onSessionLost) => {
  onSessionLostHandler = onSessionLost;

  if (interceptorsConfigured) return;
  interceptorsConfigured = true;

  api.interceptors.request.use(
    (config) => {
      const token = sessionStorage.getItem('token');

      if (token && isTokenExpired(token)) {
        clearAuthSession();
        onSessionLostHandler?.();
        navigate('/login', { replace: true });
        return Promise.reject(new Error('Token expirado, redirecionando para login'));
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';

      if (status === 401 && !isAuthEndpoint(url)) {
        clearAuthSession();
        onSessionLostHandler?.();
        navigate('/login', { replace: true });
      }

      return Promise.reject(error);
    }
  );
};

// Autenticação
export const login = (data) => api.post('/account/revenda/login', data);

export const loginDistribuidor = (data) => api.post('/account/distribuidor/login', data);

export const listarPerfisRevenda = (token) =>
  api.get('/perfis-revenda', { headers: { Authorization: `Bearer ${token}` } });

// Clientes
export const cadastrarCliente = (data, token) => 
  api.post('/clientes', data, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientes = (token) =>
  api.get('/clientes', { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesPaginado = (params = {}, token) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");

  return api.get(`/clientes/licencas?${query.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const listarClientesTodos = async (token, opts = {}) => {
  const pageSize = opts.pageSize || 2000; 
  const sortBy = opts.sortBy || 'razaosocial';
  const desc = opts.desc || false;
  const search = opts.search || '';

  let page = 1;
  let all = [];
  let totalPages = 1;

  do {
    const res = await listarClientesPaginado(
      { page, pageSize, sortBy, desc, search },
      token
    );

    const data = res.data;
    const items = Array.isArray(data?.items) ? data.items : [];
    all = all.concat(items);

    totalPages = Number(data?.totalPages || 1);
    page += 1;
  } while (page <= totalPages);

  return all;
};

export const obterClientePorId = (id, token) => 
  api.get(`/clientes/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const editarCliente = (id, data, token) =>
  api.put(`/clientes/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenGestao = (id, data, token) =>
  api.put(`/zengestao/bloquear-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenGestao = (id, token) =>
  api.put(`/zengestao/desbloquear-licenca/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenMobile = (id, data, token) =>
  api.put(`/zenmobile/bloquear-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenMobile = (id, token) =>
  api.put(`/zenmobile/desbloquear-licenca/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const excluirCliente = (id, token) =>
  api.delete(`/clientes/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesZenMobile = (token, params = {}) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");
  if (params.ativo !== undefined && params.ativo !== null) query.set("ativo", params.ativo);

  const qs = query.toString();
  return api.get(`/zenmobile${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const cadastrarClienteZenGestao = (data, token) =>
  api.post('/zengestao', data, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesZenGestao = (token, params = {}) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");
  if (params.ativo !== undefined && params.ativo !== null) query.set("ativo", params.ativo);

  const qs = query.toString();
  return api.get(`/zengestao${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const cadastrarClienteZenMobile = (data, token) =>
  api.post('/zenmobile', data, { headers: { Authorization: `Bearer ${token}` } });

export const obterClienteZenMobile = (id, token) =>
    api.get(`/zenmobile/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarClienteZenMobile = (id, data, token) =>
    api.put(`/zenmobile/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const obterClienteZenGestao = (id, token) =>
    api.get(`/zengestao/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarClienteZenGestao = (id, data, token) =>
    api.put(`/zengestao/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

// Módulos de licenciamento
export const listarModulos = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.produto) q.set("produto", params.produto);
  if (params.tipo) q.set("tipo", params.tipo);
  if (params.ativo !== undefined && params.ativo !== null) q.set("ativo", params.ativo);
  const qs = q.toString();
  return api.get(`/modulos${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const obterModulosLicenca = (licencaTipo, licencaId, token) =>
  api.get(`/licenca-modulos/${licencaTipo}/${licencaId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const atualizarModulosLicenca = (licencaTipo, licencaId, itens, token) =>
  api.put(
    `/licenca-modulos/${licencaTipo}/${licencaId}`,
    { itens },
    { headers: { Authorization: `Bearer ${token}` } }
  );

export const listarZenGestaoEmpresas = (zenGestaoId, token) =>
  api.get(`/zengestao/${zenGestaoId}/empresas`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const criarZenGestaoEmpresa = (zenGestaoId, data, token) =>
  api.post(`/zengestao/${zenGestaoId}/empresas`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const inativarZenGestaoEmpresa = (zenGestaoId, empresaId, token) =>
  api.delete(`/zengestao/${zenGestaoId}/empresas/${empresaId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const atualizarZenGestaoEmpresa = (zenGestaoId, empresaId, data, token) =>
  api.put(`/zengestao/${zenGestaoId}/empresas/${empresaId}`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const excluirZenGestaoEmpresa = inativarZenGestaoEmpresa;

export const obterRevendaMinha = (token) =>
  api.get("/revendas/minha", { headers: { Authorization: `Bearer ${token}` } });

export const obterPrecosModulosRevenda = (token) =>
  api.get("/revendas/minha/precos-modulos", { headers: { Authorization: `Bearer ${token}` } });

export const relatorioMovimentacoes = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.de) q.set("de", params.de);
  if (params.ate) q.set("ate", params.ate);
  if (params.produto) q.set("produto", params.produto);
  if (params.tipo) q.set("tipo", params.tipo);
  const qs = q.toString();
  return api.get(`/relatorios/movimentacoes${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const relatorioFaturamentoRevenda = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.ano) q.set("ano", params.ano);
  if (params.mes) q.set("mes", params.mes);
  if (params.aplicarCarencia != null) q.set("aplicarCarencia", params.aplicarCarencia);
  const qs = q.toString();
  return api.get(`/relatorios/faturamento-revenda${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

// PDVs
export const listarPdvs = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.ativo !== undefined && params.ativo !== null) q.set("ativo", params.ativo);
  return api.get(`/pdvs?${q.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
};

export const gerarLicenca = (id, data, token) =>
  api.put(`/pdvs/gerar-chave/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const baixarChave = (id, token) =>
  api.get(`/pdvs/${id}/baixar-chave`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });

export const cadastrarPdv = (data, token) => 
  api.post('/pdvs', data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirPdv = (id, token) =>
  api.delete(`/pdvs/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const obterPdvPorId = (id, token) =>
  api.get(`/pdvs/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const editarPdv = (id, data, token) =>
  api.put(`/pdvs/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearLicencaPdv = (id, data, token) =>
  api.put(`/pdvs/bloquear-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearLicencaPdv = (id, token) =>
  api.put(`/pdvs/desbloquear-licenca/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

// Home
export const getRevendaDashboardOverview = (token, params) =>
  api.get("/revenda/dashboard/overview", { headers: { Authorization: `Bearer ${token}` }, params });

export const getRevendaDashboardTopSystems = (token, params) =>
  api.get("/revenda/dashboard/top-systems", { headers: { Authorization: `Bearer ${token}` }, params });

export const getRevendaDashboardTimeline = (token, params) =>
  api.get("/revenda/dashboard/timeline", { headers: { Authorization: `Bearer ${token}` }, params });


// zentag
export const listarClientesZenTag = (token, params) =>
  api.get("/zentag", {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });

export const obterClienteZenTagPorId = (id, token) =>
  api.get(`/zentag/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const ativarClienteZenTag = (data, token) =>
  api.post("/zentag/ativar", data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenTag = (id, data, token) =>
  api.put(`/zentag/bloquear/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenTag = (id, token) =>
  api.put(`/zentag/desbloquear/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const editarClienteZenTag = (id, data, token) =>
  api.put(`/zentag/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirClienteZenTag = (id, token) =>
  api.delete(`/zentag/${id}`, { headers: { Authorization: `Bearer ${token}` } });

// zentag - autenticador (get or create)
export const obterOuCriarAutenticadorZenTag = (licId, token) =>
  api.get(`/zentag/${licId}/autenticador`, { headers: { Authorization: `Bearer ${token}` } });

// zentag - autenticador (regerar)
export const regerarAutenticadorZenTag = (licId, token) =>
  api.put(`/zentag/${licId}/autenticador/regerar`, null, { headers: { Authorization: `Bearer ${token}` } });

// zentag - usuarios (CRUD básico)
export const listarUsuariosZenTagDoCliente = (licId, token) =>
  api.get(`/zentag/${licId}/usuarios`, { headers: { Authorization: `Bearer ${token}` } });

export const criarUsuarioZenTagDoCliente = (licId, data, token) =>
  api.post(`/zentag/${licId}/usuarios`, data, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarUsuarioZenTagDoCliente = (licId, usuarioId, data, token) =>
  api.put(`/zentag/${licId}/usuarios/${usuarioId}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesZenPass = (token, params) =>
  api.get("/zenpass", { params, headers: { Authorization: `Bearer ${token}` } });

export const obterClienteZenPassPorId = (id, token) =>
  api.get(`/zenpass/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const ativarClienteZenPass = (data, token) =>
  api.post("/zenpass/ativar", data, { headers: { Authorization: `Bearer ${token}` } });

export const editarClienteZenPass = (id, data, token) =>
  api.put(`/zenpass/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenPass = (id, data, token) =>
  api.put(`/zenpass/bloquear/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenPass = (id, token) =>
  api.put(`/zenpass/desbloquear/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const bonificarLicencaZenPass = (id, data, token) =>
  api.put(`/zenpass/bonificar-licenca/${id}`, data || {}, { headers: { Authorization: `Bearer ${token}` } });

export const desbonificarLicencaZenPass = (id, token) =>
  api.put(`/zenpass/desbonificar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const cancelarLicencaZenPass = (id, data, token) =>
  api.put(`/zenpass/cancelar-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const reativarLicencaZenPass = (id, token) =>
  api.put(`/zenpass/reativar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const listarUsuariosZenPass = (licId, token) =>
  api.get(`/zenpass/${licId}/usuarios`, { headers: { Authorization: `Bearer ${token}` } });

export const criarUsuarioZenPass = (licId, data, token) =>
  api.post(`/zenpass/${licId}/usuarios`, data, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarUsuarioZenPass = (licId, usuarioId, data, token) =>
  api.put(`/zenpass/${licId}/usuarios/${usuarioId}`, data, { headers: { Authorization: `Bearer ${token}` } });

// Usuários
export const listarUsuarios = (token, params) =>
  api.get("/revendas/usuarios", { params, headers: { Authorization: `Bearer ${token}` } });

export const obterUsuarioPorId = (id, token) =>
  api.get(`/revendas/usuarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const cadastrarUsuario = (data, token) =>
  api.post("/revendas/usuarios", data, { headers: { Authorization: `Bearer ${token}` } });

export const editarUsuario = (id, data, token) =>
  api.put(`/revendas/usuarios/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirUsuario = (id, token) =>
  api.delete(`/revendas/usuarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const desativarUsuario = (id, token) =>
  api.put(`/revendas/usuarios/${id}/desativar`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const ativarUsuario = (id, token) =>
  api.put(`/revendas/usuarios/${id}/ativar`, {}, { headers: { Authorization: `Bearer ${token}` } });

// Vendedores
export const cadastrarVendedor = (data, token) =>
  api.post('/vendedores', data, { headers: { Authorization: `Bearer ${token}` } });

export const listarVendedoresTodos = async (token, opts = {}) => {
  const pageSize = opts.pageSize || 2000;
  const sortBy = opts.sortBy || 'nome';
  const desc = opts.desc || false;
  const search = opts.search || '';

  let page = 1;
  let all = [];
  let totalPages = 1;

  do {
    const res = await listarVendedoresPaginado(
      { page, pageSize, sortBy, desc, search },
      token
    );

    const data = res.data;
    const items = Array.isArray(data?.items) ? data.items : [];
    all = all.concat(items);

    totalPages = Number(data?.totalPages || 1);
    page += 1;
  } while (page <= totalPages);

  return all;
};

export const listarVendedoresPaginado = (params = {}, token) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");

  return api.get(`/vendedores?${query.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const obterVendedorPorId = (id, token) =>
  api.get(`/vendedores/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const editarVendedor = (id, data, token) =>
  api.put(`/vendedores/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirVendedor = (id, token) =>
  api.delete(`/vendedores/${id}`, { headers: { Authorization: `Bearer ${token}` } });

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const faturamentoCobrancaDistribuidor = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.ano) q.set("ano", params.ano);
  if (params.mes) q.set("mes", params.mes);
  q.set("aplicarCarencia", "true");
  return api.get(`/distribuidores/minha/faturamento-revendas?${q}`, auth(token));
};

export const faturamentoDistribuidor = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.ano) q.set("ano", params.ano);
  if (params.mes) q.set("mes", params.mes);
  q.set("aplicarCarencia", "true");
  return api.get(`/distribuidores/minha/faturamento?${q}`, auth(token));
};

export const faturamentoComposicaoRevenda = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.ano) q.set("ano", params.ano);
  if (params.mes) q.set("mes", params.mes);
  q.set("aplicarCarencia", "true");
  return api.get(`/relatorios/faturamento-composicao?${q}`, auth(token));
};

export const listarRevendasDistribuidor = (token) =>
  api.get("/distribuidores/minha/revendas", auth(token));

export const criarRevendaDistribuidor = (data, token) =>
  api.post("/distribuidores/minha/revendas", data, auth(token));

export const atualizarRevendaDistribuidor = (id, data, token) =>
  api.put(`/distribuidores/minha/revendas/${id}`, data, auth(token));

export const listarUsuariosDistribuidor = (token) =>
  api.get("/distribuidores/minha/usuarios", auth(token));

export const criarUsuarioDistribuidor = (data, token) =>
  api.post("/distribuidores/minha/usuarios", data, auth(token));

export const atualizarUsuarioDistribuidor = (id, data, token) =>
  api.put(`/distribuidores/minha/usuarios/${id}`, data, auth(token));

export const excluirUsuarioDistribuidor = (id, token) =>
  api.delete(`/distribuidores/minha/usuarios/${id}`, auth(token));

export const listarClientesDistribuidor = (token) =>
  api.get("/distribuidores/minha/clientes", auth(token));

export const criarClienteDistribuidor = (data, token) =>
  api.post("/distribuidores/minha/clientes", data, auth(token));

export const listarTabelasDistribuidor = (token) =>
  api.get("/distribuidores/minha/tabelas-preco", auth(token));

export const criarTabelaDistribuidor = (data, token) =>
  api.post("/distribuidores/minha/tabelas-preco", data, auth(token));

export const obterTabelaDistribuidor = (id, token) =>
  api.get(`/distribuidores/minha/tabelas-preco/${id}`, auth(token));

export const atualizarTabelaDistribuidor = (id, data, token) =>
  api.put(`/distribuidores/minha/tabelas-preco/${id}`, data, auth(token));

export const listarModulosCatalogo = (token) =>
  api.get("/modulos", { ...auth(token), params: { ativo: true } });

export default api;
